import _ from 'lodash';
import type { GetPrimeXPolicyDetailsByViewResponse } from '../../../../../api/policies.api.types';
import type { CoverageDto } from '../../../../../types';
import { convertDateUTC } from '../../../../../utils/date.utils';
import type { GetPresetsResponse } from '../../../../../api/rater.api';

export const getSelectedSteps = (selectedSteps: Record<string, boolean>) => {
  return Object.keys(selectedSteps).filter((stepId) => selectedSteps[stepId]);
};

export const deriveFormattedPolicyDetails = (
  policy: GetPrimeXPolicyDetailsByViewResponse | undefined
) => {
  if (!policy) {
    return {
      coverages: [],
    };
  }

  return {
    companyName: policy.policy.companyName ?? '',
    naicsCode: policy.firmographicData?.naicsCode ?? '',
    naicsDescription: policy.firmographicData?.primaryIndustry ?? '',
    minEndorsementStartDate: convertDateUTC(
      policy.policy?.effectiveDate as number,
      'UTC'
    ),
    maxEndorsementStartDate: convertDateUTC(
      policy.policy?.endDate as number,
      'UTC'
    ),
    address1: policy.firmographicData?.address1 ?? '',
    address2: policy.firmographicData?.address2 ?? '',
    city: policy.firmographicData?.city ?? '',
    state: policy.firmographicData?.state?.toUpperCase() ?? '',
    zipCode: policy.firmographicData?.zipCode ?? '',
    additionalInsureds: policy.policy?.additionalInsureds ?? [],
    accountId: policy.firmographicData?.accountId ?? '',
    coverages: (policy.policy?.coverages as unknown as CoverageDto[]) ?? [],
    aggregateLimit: policy.policy?.limit ?? '',
    deductible: policy.policy?.deductible ?? '',
    retroactivePeriod: policy.policy?.retroactivePeriod ?? '',
    waitingPeriod: policy.policy?.waitingPeriod ?? '',
    version: policy.policy?.version ?? '',
    revision: policy.policy?.revision ?? '',
    totalPremium: policy.policy?.totalPremium ?? '',
    policyEndDate: policy.policy?.endDate,
    firmographicData: policy.firmographicData,
    hasTria: policy.policy?.hasTria,
    id: policy.policy.id,
    policyNumber: policy.policy.policyNumber,
    quoteId: policy.policy.quoteId,
    raterResponse: policy.policy.raterResponse,
    productType: policy.policy.product,
    agencyId: policy.policy.agencyId,
    accountGroupId: policy.policy.accountGroupId,
    accountGroupName: policy.policy.accountGroupName,
    accountGroupType: policy.policy.accountGroupType,
    teamIds: policy.policy.teamIds,
    teamNames: policy.policy.teamNames,
    // ISO 4217 code (MS-270) — never re-derived from productType/region.
    currency: policy.currency,
  };
};

export const syncCoveragesWithPresets = <
  T extends { coverages: CoverageDto[] }
>(
  presets: GetPresetsResponse['coverageGroups'],
  data: T
) => {
  const clonedData = _.cloneDeep(data);

  const coverageMap = clonedData?.coverages?.reduce((acc, coverage) => {
    return {
      ...acc,
      [coverage.name]: coverage,
    };
  }, {} as any);

  Object.keys(presets).forEach((coverageGroup) => {
    presets[coverageGroup].forEach((coverage) => {
      if (coverage.coverageName in coverageMap) {
        coverageMap[coverage.coverageName].groupName = coverageGroup;
        coverageMap[coverage.coverageName].isMandatory =
          coverage.rules.isMandatory;
        coverageMap[coverage.coverageName].isAvailable =
          coverage.preset.isAvailable;
        coverageMap[coverage.coverageName].coverageFullName =
          coverage.coverageFullName;
      }
    });
  });

  const hydratedCoverages = Object.values(coverageMap) as T['coverages'];
  clonedData.coverages = hydratedCoverages;

  return clonedData;
};
